import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { AuthAPI } from "@/lib/api";
import logger from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const u = await AuthAPI.me();
      setUser(u);
    } catch (err) {
      if (err?.response?.status !== 401) {
        logger.warn("Auth /me failed", err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const signOut = async () => {
    try {
      await AuthAPI.logout();
    } catch (err) {
      logger.warn("Logout request failed", err);
    }
    try {
      localStorage.removeItem("session_token");
    } catch (err) {
      logger.warn("localStorage unavailable on sign-out", err);
    }
    setUser(null);
    window.location.replace("/login");
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
